import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { url, FETCH_CREDENTIALS } from './apiClient';
import LanguageSwitcher from './LanguageSwitcher';
import Icon from './Icons';
import './Auth.css';

function Login({ setUser }) {
  const { t } = useTranslation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Shown once when authFetch redirected here after a 401 (e.g. logged out
  // because this account signed in elsewhere) -- read once, then discarded.
  const [error, setError] = useState(() => {
    const notice = sessionStorage.getItem('loginNotice');
    if (notice) sessionStorage.removeItem('loginNotice');
    return notice || '';
  });
  const navigate = useNavigate();

  const handleLogin = async () => {
    setError('');
    setSubmitting(true);
    try {
      const endpoint = url.login();
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
        credentials: FETCH_CREDENTIALS
      });
      const raw = await res.text();
      let data; try { data = raw ? JSON.parse(raw) : {}; } catch { data = { error: raw }; }
      if (!res.ok) {
        if (data.error === 'Registration incomplete') {
          setError('Registration incomplete. Click Register.');
          return;
        }
        throw new Error(data.error || `Login failed (HTTP ${res.status})`);
      }
      if (!data.role) data.role = 'TENANT';
      if (!data.username) data.username = data.userName || data.name || username;
      data.lastActivity = Date.now();
      setUser(data);
      localStorage.setItem('user', JSON.stringify(data));
      if (data.role === 'ADMIN') navigate('/admin/dashboard'); else navigate('/');
    } catch (e) {
      if (e.name === 'TypeError' && (e.message === 'Failed to fetch' || e.message === 'NetworkError when attempting to fetch resource.')) {
        setError('Could not reach the server. Please check your connection and try again.');
        return;
      }
      // The backend's own message is already user-facing (e.g. "Invalid
      // credentials", or "Too many failed login attempts. Try again in Ns."
      // from the account lockout) -- swallowing it into a generic "Server
      // error" would make a locked-out user think the whole server is down.
      if ((e.message || '').toLowerCase().includes('registration incomplete')) {
        setError('Registration incomplete. Click Register.');
      } else {
        setError(e.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth">
      <div className="auth__lang"><LanguageSwitcher /></div>
      <div className="auth__card">
        <div className="auth__logo"><img src="/vgr-logo.png" alt="VGR" /></div>
        <p className="auth__eyebrow">{t('login.welcome')}</p>
        <h1 className="auth__title">{t('login.login')}</h1>
        <p className="auth__sub">{t('login.subtitle')}</p>

        {error && <div className="auth__error" role="alert">{error}</div>}

        <form
          className="auth__form"
          onSubmit={(e) => { e.preventDefault(); if (!submitting) handleLogin(); }}
        >
          <label className="auth__field">
            <Icon name="users" size={18} />
            <input
              placeholder={t('login.username')}
              aria-label={t('login.username')}
              autoComplete="username"
              autoCapitalize="none"
              value={username}
              onChange={e => setUsername(e.target.value)}
            />
          </label>
          <label className="auth__field">
            <Icon name="key" size={18} />
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder={t('login.password')}
              aria-label={t('login.password')}
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
            />
            <button type="button" className="auth__peek" onClick={() => setShowPassword(v => !v)}>
              {showPassword ? t('login.hide') : t('login.show')}
            </button>
          </label>
          <div className="auth__row">
            <button type="button" className="auth__link" onClick={() => navigate('/forgot')}>{t('login.forgot')}</button>
          </div>
          <button type="submit" className="auth__submit" disabled={submitting}>
            {submitting ? '…' : t('login.login')}
          </button>
        </form>

        <div className="auth__divider"><span>{t('login.newHere')}</span></div>
        <button type="button" className="auth__secondary" onClick={() => navigate('/register')}>{t('login.register')}</button>

        <p className="auth__legal">
          {t('login.agree')} <Link to="/terms">{t('home.terms')}</Link> {t('login.and')} <Link to="/privacy">{t('home.privacy')}</Link>
        </p>
        <p className="auth__legal auth__about"><Link to="/about">{t('login.about')}</Link></p>
      </div>
      <div className="auth__secure"><Icon name="shield" size={16} /> {t('login.secure')}</div>
    </div>
  );
}

export default Login;